import { computed, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { Auth, initialState } from './auth.model';
import { AuthApiService } from './auth-api.service';
import { UserService } from '../services/user.service';

/** Extracts the API error payload, unwrapping the JSON body when it arrives as a Blob (blob responseType). */
async function parseErrorPayload(error: unknown): Promise<any> {
  const err = error as HttpErrorResponse;
  if (err?.error instanceof Blob) {
    try {
      return JSON.parse(await err.error.text());
    } catch {
      return { message: 'An unknown error occurred.' };
    }
  }
  return err?.error ?? { message: 'An unknown error occurred.' };
}

/** Returns the stored JWT if it has not expired; clears it from localStorage otherwise. */
function readUnexpiredToken(): string | null {
  const token = window.localStorage.getItem('token');
  if (!token) {
    return null;
  }
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload.exp || payload.exp * 1000 > Date.now()) {
      return token;
    }
  } catch {}
  window.localStorage.removeItem('token');
  return null;
}

export const AuthStore = signalStore(
  withState(initialState),
  withComputed(({userSignal}) => ({
    userLoggedInSignal: computed(() => !!userSignal),
  })),
  withMethods((
    store,
    authApiService = inject(AuthApiService),
    userService = inject(UserService),
  ) => ({
    setUser(user: Auth["userSignal"]) {
      patchState(store, {userSignal: user});
    },
    setJwt(jwt: Auth["jwtSignal"]) {
      patchState(store, {jwtSignal: jwt});
    },
    setUserIsLoggedIn(isLoggedIn: Auth["userIsLoggedInSignal"]) {
      patchState(store, {userIsLoggedInSignal: isLoggedIn});
    },
    setLoading(isLoading: boolean) {
      patchState(store, {isLoadingSignal: isLoading});
    },
    resetStore() {
      patchState(store, initialState);
    },

    /** Step 1: validate credentials (determines whether 2FA setup or code entry is next). */
    async loginValidate(email: string, password: string) {
      patchState(store, { isLoadingSignal: true });
      try {
        const qrBlob = await authApiService.loginValidation(email, password);
        return { success: true, qrBlob, error: undefined as any };
      } catch (error: unknown) {
        return { success: false, qrBlob: null as Blob | null, error: await parseErrorPayload(error) };
      } finally {
        patchState(store, { isLoadingSignal: false });
      }
    },

    /** Step 2: complete login with the 2FA code, then load the user. */
    async login(email: string, password: string, verificationCode: string) {
      patchState(store, { isLoadingSignal: true });
      try {
        const jwt = await authApiService.login2fa(email, password, verificationCode);
        if (jwt) {
          window.localStorage.setItem('token', jwt);
          patchState(store, { jwtSignal: jwt });
        }
        const user = await firstValueFrom(userService.getUser());
        patchState(store, { userSignal: user, userIsLoggedInSignal: !!user });
        return { success: true, error: undefined as any };
      } catch (error: unknown) {
        return { success: false, error: await parseErrorPayload(error) };
      } finally {
        patchState(store, { isLoadingSignal: false });
      }
    },
  })),
  withHooks({
    onInit(store) {
      const token = readUnexpiredToken();
      if (token) {
        patchState(store, { jwtSignal: token, userIsLoggedInSignal: true });
      }
    },
  })
);
